import { Database } from 'lucide-react'
import { QueryUsecaseTableResult, UsecaseTablesMap } from 'poc-ai-biz-irdb_domain-model'
import {
  UsecaseTableCompanyOverviewOrganism,
  UsecaseTableBsHighlightOrganism,
  UsecaseTablePlHighlightOrganism,
  UsecaseTableCfHighlightOrganism,
  UsecaseTableExecutiveOverviewOrganism,
  UsecaseTableExecutivesOrganism,
  UsecaseTableExecutiveCompensationOrganism,
  UsecaseTableBusinessDescriptionOrganism,
  UsecaseTableBusinessRisksOrganism,
  UsecaseTableHistoryOrganism,
  UsecaseTableMajorSubsidiariesOrganism,
  UsecaseTableOfficesOrganism,
  UsecaseTableMajorShareholdersOrganism,
} from '../../../../organisms/UsecaseTableOrganism/_gen'
import { getUsecaseTableAppearanceConfiguration, UsecaseTableMapKeys } from '../../model/usecaseTableAppearanceConfiguration'

export type SourceDocumentQueryUsecaseTableCardPropsContent = {
  result: QueryUsecaseTableResult
  displayName: string
  table: {
    tableName: keyof UsecaseTablesMap
    displayName: string
    icon: React.ReactNode
  }
  usecaseTablesMap: UsecaseTablesMap
  totalDataCount: number
}

export type SourceDocumentQueryUsecaseTableCardProps = SourceDocumentQueryUsecaseTableCardPropsContent & {
  isExpanded: boolean
  onClick: () => void
}

/**
 * QueryUsecaseTableResultをUIカード表示用のプロパティに変換する
 *
 * @param queryUsecaseTableResult - 単一テーブル種別に複数企業のデータを含む結果
 * @returns UIカード表示用のプロパティ（isExpanded, onClickは含まない）
 */
export const mapToSourceDocumentQueryUsecaseTableCardContent = (
  queryUsecaseTableResult: QueryUsecaseTableResult,
): SourceDocumentQueryUsecaseTableCardPropsContent => {
  const tableNameAsUsecaseTablesMapKey = queryUsecaseTableResult.tableName as keyof UsecaseTablesMap

  const appearanceConfiguration = getUsecaseTableAppearanceConfiguration(tableNameAsUsecaseTablesMapKey)

  return {
    result: queryUsecaseTableResult,
    displayName: appearanceConfiguration.displayName,
    table: {
      tableName: tableNameAsUsecaseTablesMapKey,
      displayName: appearanceConfiguration.displayName,
      icon: appearanceConfiguration.icon,
    },
    usecaseTablesMap: {
      [queryUsecaseTableResult.tableName]: queryUsecaseTableResult.data,
    } as UsecaseTablesMap,
    totalDataCount: queryUsecaseTableResult.data.length,
  }
}

export const SourceDocumentQueryUsecaseTableCard: React.FC<SourceDocumentQueryUsecaseTableCardProps> = ({
  displayName,
  table,
  usecaseTablesMap,
  totalDataCount,
  isExpanded,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className="block p-3 border border-gray-200 rounded-md bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
    >
      <div className="flex items-start gap-2">
        <Database className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800">{displayName}</div>
          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-1">
            {!isExpanded ? (
              <span className="inline-flex items-center gap-1">
                {table.icon}
                {table.displayName}
              </span>
            ) : (
              (() => {
                console.log('Rendering expanded table:', { table, displayName, usecaseTablesMap })
                switch (table.tableName) {
                  case 'usecaseTableCompanyOverview': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableCompanyOverviewOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableBsHighlight': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableBsHighlightOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTablePlHighlight': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTablePlHighlightOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableCfHighlight': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableCfHighlightOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableExecutiveOverview': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableExecutiveOverviewOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableExecutives': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableExecutivesOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableExecutiveCompensation': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableExecutiveCompensationOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableBusinessDescription': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableBusinessDescriptionOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableBusinessRisks': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableBusinessRisksOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableHistory': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableHistoryOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableMajorSubsidiaries': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableMajorSubsidiariesOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableOffices': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableOfficesOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  case 'usecaseTableMajorShareholders': {
                    const contents = usecaseTablesMap[table.tableName]
                    return contents && <UsecaseTableMajorShareholdersOrganism defaultExpanded={true} status="completed" contents={contents} />
                  }
                  default:
                    console.error(`Unknown table name: ${table.tableName}`)
                    return null
                }
              })()
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">{totalDataCount}件</div>
        </div>
      </div>
    </button>
  )
}
